import { PrecedentMapModel } from "../models/precedent-map";
import { autoinject, transient } from "aurelia-dependency-injection";
import { backboneViewProperties } from "../../decorators/backbone-properties";
import "spin/dist/spin";
import { AppEvents } from "../../app-events";

interface ISizeAndPositionOptions {
    x: number;
    y: number;
    r: number;
    opacity?: number;
    animate?: boolean;
}

export interface ILayers {
    citedNodesLayer: ISnapElement;
    citingNodesLayer: ISnapElement;
    mainNodeLayer: ISnapElement;
    linesLayer: ISnapElement;
    commonRelationshipLinesLayer: ISnapElement;
    labelLayer: ISnapElement;
    thinkingLayer: ISnapElement;
}

@autoinject
@transient()
@backboneViewProperties({el: "#precedent-map-region"})
export class PrecedentMapView extends Backbone.View<PrecedentMapModel> {

    public origin: PrecedentMapModel;
    public thinking: JQuery;
    private centreX: number;
    private centreY: number;
    private layers: ILayers;
    private mainRadius: number;
    private surface: any;
    private element: ISnapElement;
    private transitionTime = 400;
    private clickMode: number;
    private map: ISnapElement;

    constructor(public appEvents: Backbone.Events, public model: PrecedentMapModel) {
        super();
    }

    public init(id, unique, surface) {
        this.model.DocKey = id;
        this.model.UniqueId = unique;
        this.surface = surface;
        this.setupSize();
        this.createLayers();
        this.setupModelChangeListeners();
    }

    public events(): Backbone.EventsHash {
        const events: Backbone.EventsHash = {};
        events["click .sign-in-link"] = "signinLinkClick";
        return events;
    }

    public clean() {
        this.layers.linesLayer.clear();
        this.layers.citedNodesLayer.clear();
        this.layers.citingNodesLayer.clear();
        this.layers.labelLayer.clear();
        this.layers.commonRelationshipLinesLayer.clear();
        this.layers.citingNodesLayer.insertBefore(this.layers.mainNodeLayer, this.layers.citingNodesLayer);
    }

    public create(origin?: PrecedentMapModel) {
        return new Promise((resolve, reject) => {
            this.createMainNode();
            if (origin) {
                this.setInitialScale(origin);
            }
            this.zoomToFullScale();
            this.drawLoader();
            this.styleMainNode();
            this.model.loadData().then(() => {
                this.model.trigger("redraw");
                this.thinking.empty();
                this.appEvents.trigger(AppEvents.PrecedentMapLoaded, this.model);
                resolve();
            });
        });
    }

    public drawLoader() {
        const opts = { lines: 13, length: 20, width: 2, radius: 20, corners: 1, rotate: 0, rection: 1, color: "#000", speed: 1.1, trail: 74, shadow: false, hwaccel: true, className: "spinner", zIndex: 2e9, top: "auto", left: "auto" };
        const spinner = new Spinner(opts).spin();
        this.thinking = $('<div class="pmap-loader"><div>').append(spinner.el);
        $("#precedent-map-region").append(this.thinking);
    }

    public disappear() {
        this.unbind();
        (this.map.node as any).style.opacity = 0;
        setTimeout(() => {
            this.map.remove();
        }, 1000);
    }

    private createLayers() {
        this.map = this.surface.group().addClass("case-map");
        this.layers = {
            citingNodesLayer: this.map.group().addClass("citing-nodes"),
            linesLayer: this.map.group().addClass("lines"),
            mainNodeLayer: this.map.group().addClass("main-node"),
            commonRelationshipLinesLayer: this.map.group().addClass("lines"),
            citedNodesLayer: this.map.group().addClass("cited-nodes"),
            labelLayer: this.map.group().addClass("labels"),
            thinkingLayer: this.map.group()
        };
    }

    private setupSize() {
        this.centreX = this.surface.attr("width") / 2;
        this.centreY = (this.surface.attr("height") / 2) + 50;
        this.mainRadius = this.surface.attr("height") * 0.28;
        this.model.SizeAndPosition = { x: this.centreX, y: this.centreY, r: this.mainRadius };
    }

    private createMainNode() {
        this.element = this.layers.mainNodeLayer.circle(this.centreX, this.centreY, this.mainRadius);
    }

    private setInitialScale(origin: PrecedentMapModel) {
        const pos = origin.SizeAndPosition;
        const scale = pos.r / this.mainRadius;
        (this.map.node as any).style.transformOrigin = pos.x + "px " + pos.y + "px";
        (this.map.node as any).style.transform = "scale(" + scale + ")";
    }

    private zoomToFullScale() {
        this.map.addClass("animated");
        _.defer(() => {
            (this.map.node as any).style.transform = "scale(1)";
        });
    }

    private styleMainNode() {
        this.element.attr("class", "precedent-map-node focus");
        this.addSVGStyles(this.element);
    }

    private drawRelations(animate) {
        this.clean();
        this.transitionTime = animate ? 400 : 0;
        const citedTransition = this.drawCitedNodes();
        const citingTransition = this.drawCitingNodes();
        $.when(citedTransition, citingTransition).done(() => {
            this.bringCitingNodesToFront();
            this.drawFocusConnections();
        });
    }

    private bringCitingNodesToFront() {
        this.layers.citingNodesLayer.insertBefore(this.layers.labelLayer, this.layers.citingNodesLayer);
    }

    private drawFocusConnections() {
        this.model.DisplayCitingNodes.each((citingModel) => {
            const description = citingModel.TreatmentDescription;
            const lineTitle = document.createElementNS("http://www.w3.org/2000/svg", "title");
            lineTitle.textContent = description;
            const line = this.drawRelationship(citingModel, this.model, citingModel.TreatmentType);
            const arrow = this.drawArrow(citingModel, this.model, citingModel.TreatmentType);
            const title = document.createElementNS("http://www.w3.org/2000/svg", "title");
            title.textContent = description;
            arrow.append(title);
            line.append(lineTitle);
            const connection = this.layers.linesLayer.group(line, arrow).attr("opacity", 0);
            connection.animate({ opacity: 1 }, 500);
        });
    }

    private clearFocusConnections() {
        this.layers.linesLayer.clear();
    }

    private addSVGStyles(element) {
        const gradientUrl = window.location + "#gradient";
        const shadowUrl = window.location + "#innerShadow";
        let fill;

        fill = "url(" + gradientUrl + ")";

        element.attr({
            fill,
            filter: "url(" + shadowUrl + ")"
        });
    }

    private drawCitedNodes(): Promise<any> {
        return new Promise((resolve, reject) => {
            this.model.computeCitedNodesPosition(this.centreX, this.centreY, this.mainRadius);
            this.drawNodeCollection(this.model.DisplayCitedNodes, "cited", resolve);
        });
    }

    private drawCitingNodes() {
        return new Promise((resolve, reject) => {
            this.model.computeCitingNodesPosition(this.centreX, this.centreY, this.mainRadius);
            this.drawNodeCollection(this.model.DisplayCitingNodes, "citing", resolve);
        });
    }

    private drawNodeCollection(collection: Backbone.Collection<PrecedentMapModel>, direction: string, resolve: (value?: {} | PromiseLike<{}>) => void) {
        if (collection.size() === 0) {
            resolve();
        }
        collection.each((node) => {
            this.DrawNode(node, direction);
        });
        resolve();
    }

    private DrawNode(node: PrecedentMapModel, direction: string) {
        const className = this.nodeClassName(node.TreatmentType, direction);
        const layer = direction === "cited" ? this.layers.citedNodesLayer : this.layers.citingNodesLayer;

        const element = this.AddCircleToLayer(layer, node.SizeAndPosition).attr("class", className);

        const fullTitle = document.createElementNS("http://www.w3.org/2000/svg", "title");
        fullTitle.textContent = node.Title;
        element.append(fullTitle);
        this.addSVGStyles(element);
        node.SvgElement = element;
        this.drawLabel(node);
        this.setupClickHandler(node);
    }

    private AddCircleToLayer(layer: ISnapElement, sizeAndPosition: ISizeAndPositionOptions) {
        return layer.circle(sizeAndPosition.x, sizeAndPosition.y, sizeAndPosition.r);
    }

    private setupHover(nodeModel) {
        const element = nodeModel.SvgElement;
        element.mouseover(() => {
            this.layers.citingNodesLayer.append(element);
        });
    }

    private setupClickHandler(nodeModel: PrecedentMapModel) {
        const element = nodeModel.SvgElement;
        let clicks = 0;
        element.click((event) => {
            clicks++;
            if (clicks === 1) {
                setTimeout(() => {
                    if (clicks === 1) {
                        this.toggleCommonRelationships(nodeModel);
                    }
                    else {
                        this.origin = nodeModel;
                        const url = `precedent-map/${nodeModel.FriendlyUrlTitle}/${nodeModel.DocKey}/${nodeModel.UniqueId}`;
                        Backbone.history.navigate(url, { trigger: true });
                    }
                    clicks = 0;
                }, 300);
            }
        });
    }

    private toggleCommonRelationships(nodeModel: PrecedentMapModel) {
        const element = nodeModel.SvgElement;
        const label = nodeModel.LabelGroup;
        if (!element.hasClass("common-focus")) {
            this.clearFocusConnections();
            this.setAllAsNotCommon();
            element.addClass("common-focus");
            label.addClass("common-focus");
            this.showCommonRelationships(nodeModel, nodeModel.CitedNodes, "cited");
            this.showCommonRelationships(nodeModel, nodeModel.CitingNodes, "citing");
        } else {
            this.clearCommonRelationships();
            element.removeClass("common-focus");
            label.removeClass("common-focus");
            this.drawFocusConnections();
        }
    }

    private showCommonRelationships(nodeModel: PrecedentMapModel, relations: Backbone.Collection<PrecedentMapModel>, direction) {
        relations.each((relation) => {
            const id = relation.DocKey;
            if (id === nodeModel.DocKey) {
                return;
            }
            const commonNode = this.model.DisplayCitedNodes.where({ DocKey: id })[0] || this.model.DisplayCitingNodes.where({DocKey: id})[0];
            if (commonNode) {
                this.setCommonRelationStatus(commonNode, true, direction);
                this.drawCommonRelationshipConnection(nodeModel, relation, commonNode, direction);
            }
        });
    }

    private setAllAsNotCommon() {
        this.layers.commonRelationshipLinesLayer.clear();
        this.model.AllRelations.forEach((node) => {
            this.setCommonRelationStatus(node, false, "");
        });
    }

    private clearCommonRelationships() {
        this.layers.commonRelationshipLinesLayer.clear();
        this.model.AllRelations.forEach((node) => {
            if (!node.SvgElement || !node.LabelGroup) {
                return;
            }
            node.SvgElement.removeClass("common not-common common-focus cited citing").addClass(node.DefaultDirection);
            node.LabelGroup.removeClass("common not-common common-focus");
        });
    }

    private drawCommonRelationshipConnection(nodeModel, relation, commonNode, direction) {
        const citedModel = (direction === "citing") ? nodeModel : relation;
        const commonCitedModel = (direction === "citing") ? nodeModel : commonNode;
        const citingModel = (direction === "cited") ? nodeModel : relation;
        const commonCitingModel = (direction === "cited") ? nodeModel : commonNode;

        if (citedModel === citingModel) {
            return;
        }

        const treatmentType = relation.TreatmentType;
        const description = relation.TreatmentDescription;

        const lineTitle = document.createElementNS("http://www.w3.org/2000/svg", "title");
        lineTitle.textContent = description;

        const line = this.drawRelationship(commonCitingModel, commonCitedModel, treatmentType).append(lineTitle);

        const title = document.createElementNS("http://www.w3.org/2000/svg", "title");
        title.textContent = description;
        const arrow = this.drawArrow(commonCitingModel, commonCitedModel, treatmentType).append(title);

        const connection = this.layers.commonRelationshipLinesLayer.group(line, arrow).attr({ opacity: 0, title: description});
        connection.animate({ opacity: 1 }, 500);
        this.layers.commonRelationshipLinesLayer.append(line);
        this.layers.commonRelationshipLinesLayer.append(arrow);
    }

    private drawRelationship(citingNode: PrecedentMapModel, citedNode: PrecedentMapModel, treatmentType: number): ISnapElement {
        const citingPos = citingNode.SizeAndPosition;
        const citedPos = citedNode.SizeAndPosition;
        const line = this.layers.commonRelationshipLinesLayer.line(citingPos.x, citingPos.y, citedPos.x, citedPos.y);
        line.attr("class", this.edgeClassName(treatmentType));
        return line;
    }

    private drawArrow(citingNode: PrecedentMapModel, citedNode: PrecedentMapModel, treatmentType: number): ISnapElement {
        const citingPos = citingNode.SizeAndPosition;
        const citedPos = citedNode.SizeAndPosition;
        const xDif = citingPos.x - citedPos.x;
        const yDif = citingPos.y - citedPos.y;

        // Put the arrow half way along the visible part of the line.
        const lineLength = Math.sqrt((xDif * xDif) + (yDif * yDif));
        const visibleLineLength = lineLength - citedPos.r - citingPos.r;
        let distanceAlongHypotenuse = citedPos.r + (visibleLineLength / 2);
        // Take off 5 because the distance is to the point of the arrow and 5 seems to give a nice look.
        distanceAlongHypotenuse -= 5;

        let angle = Math.atan(yDif / xDif);
        const xDist = distanceAlongHypotenuse * Math.cos(angle);
        const yDist = distanceAlongHypotenuse * Math.sin(angle);
        let xPos;
        let yPos;
        let angleOffset;

        if (citedPos.x < citingPos.x) {
            xPos = citedPos.x + xDist;
            yPos = citedPos.y + yDist;
            angleOffset = 90;

        } else {
            xPos = citedPos.x - xDist;
            yPos = citedPos.y - yDist;
            angleOffset = -90;
        }
        angle = angle * (180 / Math.PI);
        const t = new Snap.Matrix();
        t.rotate(angle + angleOffset, xPos, yPos);
        const arrow = this.layers.commonRelationshipLinesLayer.polygon(xPos, yPos, xPos + 4, yPos - 10, xPos - 4, yPos - 10);
        arrow.transform(t);
        arrow.attr("class", this.arrowClassName(treatmentType));
        return arrow;
    }

    private setCommonRelationStatus(nodeModel, common: boolean, direction: string) {
        const element = nodeModel.SvgElement;
        const label = nodeModel.LabelGroup;
        if (!element || !label) {
            return;
        }
        element.removeClass("not-common common common-focus").toggleClass("common", common).toggleClass("not-common", !common);
        label.removeClass("not-common common common-focus").toggleClass("common", common).toggleClass("not-common", !common);
    }

    private drawLabel(nodeModel: PrecedentMapModel) {
        const pos = nodeModel.SizeAndPosition;
        const xPos = pos.x;
        const yPos = pos.y + 3;
        const backHeight = 15;
        const backOffset = 3;
        const year = nodeModel.Year || "0";
        const title = nodeModel.Title || "No name yet";
        const nameText = this.layers.labelLayer.text(xPos, yPos, title.substring(0, 17)).attr("class", "precedent-map-label");

        const yearText = this.layers.labelLayer.text(xPos, yPos + 15, year).attr("class", "precedent-map-year");
        const nameBox = nameText.getBBox();
        const backWidth = nameBox.width + 10;
        const label = this.layers.labelLayer.rect(xPos - (backWidth / 2), yPos - (backHeight / 2) - backOffset, backWidth, backHeight, 2, 2).attr("class", "precedent-map-label-rect");

        const labelGroup = this.layers.labelLayer.group(label, nameText, yearText);
        labelGroup.attr({ class: "label-group " });
        nodeModel.LabelGroup = labelGroup;
    }

    private nodeClassName(treatmentType: number, direction: string) {
        return "precedent-map-node " + this.getTreatmentClass(treatmentType) + " " + direction;
    }

    private edgeClassName(treatmentType: number) {
        return "precedent-map-edge " + this.getTreatmentClass(treatmentType);
    }

    private arrowClassName(treatmentType: number) {
        return "precedent-map-arrow " + this.getTreatmentClass(treatmentType);
    }

    private getTreatmentClass(treatmentType: number): string {
        let treatmentClass = "";
        switch (treatmentType) {
            case 1:
                treatmentClass = "positive";
                break;
            case 2:
                treatmentClass = "neutral";
                break;
            case 3:
                treatmentClass = "negative";
                break;
        }
        return treatmentClass;
    }

    private setupModelChangeListeners() {
        this.listenTo(this.model, "redraw", (animated) => { this.drawRelations(animated); });
    }

    private signinLinkClick(e: JQueryEventObject): void {
        e.preventDefault();
        const returnUrl = window.location.pathname;
        const signinUrl = `signin?return=${returnUrl}`;
        Backbone.history.navigate(signinUrl, { trigger: true });
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./src/precedent-map/views/precedent-map.ts